import React, { useState, useEffect, useContext } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Typography,
  IconButton,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { makeStyles } from "@material-ui/core/styles";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import { getStandardButtonStyles } from "../../styles/standardButtons";

const useStyles = makeStyles((theme) => ({
  ...getStandardButtonStyles(theme),
  dialogPaper: {
    borderRadius: "16px !important",
    border: `1px solid ${theme.palette.primary.main} !important`,
    boxShadow: theme.palette.type === "dark" 
      ? `0 0 16px ${theme.palette.primary.main}80 !important` 
      : `0 0 12px ${theme.palette.primary.main}40 !important`,
    backgroundColor: theme.palette.type === "dark" ? "#1E2126 !important" : "#FFFFFF !important",
  },
  dialogHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2, 2.5),
    borderBottom: `1px solid ${
      theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"
    }`,
  },
  dialogActions: {
    padding: theme.spacing(2, 2.5),
    borderTop: `1px solid ${
      theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"
    }`,
  },
  root: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
  textField: {
    marginBottom: theme.spacing(2),
  },
  formControl: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: theme.palette.primary.main,
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

const WebWidgetSchema = Yup.object().shape({
  name: Yup.string().min(2, "Nombre demasiado corto").required("Obligatorio"),
  whatsappId: Yup.number().required("Selecciona una conexión"),
  flowId: Yup.number().required("Selecciona un flujo"),
});

const WebWidgetModal = ({ open, onClose, widgetId }) => {
  const classes = useStyles();
  const { whatsApps } = useContext(WhatsAppsContext);
  const [flows, setFlows] = useState([]);
  const [initialValues, setInitialValues] = useState({
    name: "",
    color: "#7225c5",
    welcomeMessage: "Hola, ¿en qué podemos ayudarte?",
    whatsappId: "",
    flowId: "",
    logo: "",
  });

  useEffect(() => {
    fetchFlows();
    if (widgetId && open) {
      fetchWidget();
    } else {
      setInitialValues({
        name: "",
        color: "#7225c5",
        welcomeMessage: "Hola, ¿en qué podemos ayudarte?",
        whatsappId: "",
        flowId: "",
        logo: "",
      });
    }
  }, [widgetId, open]);

  const fetchFlows = async () => {
    try {
      const { data } = await api.get("/flowbuilder");
      setFlows(data.flows || []);
    } catch (err) {
      toastError(err);
    }
  };

  const fetchWidget = async () => {
    try {
      const { data } = await api.get("/web-widget");
      const widget = data.find((w) => w.id === widgetId);
      if (widget) {
        setInitialValues({
          name: widget.name || "",
          color: widget.color || "#7225c5",
          welcomeMessage: widget.welcomeMessage || "",
          whatsappId: widget.whatsappId || "",
          flowId: widget.flowId || "",
          logo: widget.logo || "",
        });
      }
    } catch (err) {
      toastError(err);
    }
  };

  const handleSave = async (values) => {
    try {
      if (widgetId) {
        await api.put(`/web-widget/${widgetId}`, values);
        toast.success("Widget actualizado con éxito");
      } else {
        await api.post("/web-widget", values);
        toast.success("Widget creado con éxito");
      }
      onClose();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: classes.dialogPaper }}
    >
      <div className={classes.dialogHeader} dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
        <Typography variant="h6" style={{ fontWeight: 600 }}>
          {widgetId ? i18n.t("globalModals.webWidget.edit") : i18n.t("globalModals.webWidget.new")}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </div>
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        validationSchema={WebWidgetSchema}
        onSubmit={(values, actions) => {
          handleSave(values);
          actions.setSubmitting(false);
        }}
      >
        {({ values, errors, touched, isSubmitting }) => (
          <Form>
            <DialogContent dividers dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
              <Field
                as={TextField}
                label="Nombre"
                name="name"
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name && errors.name}
                variant="outlined"
                margin="dense"
                fullWidth
                className={classes.textField}
              />
              <Field
                as={TextField}
                label="Color del Widget (Hex)"
                name="color"
                type="color"
                variant="outlined"
                margin="dense"
                fullWidth
                className={classes.textField}
              />
              <Field
                as={TextField}
                label="Mensaje de Bienvenida"
                name="welcomeMessage"
                multiline
                rows={2}
                variant="outlined"
                margin="dense"
                fullWidth
                className={classes.textField}
              />
              <Field
                as={TextField}
                label="Icono Personalizado (ej. /files/logo.png)"
                name="logo"
                variant="outlined"
                margin="dense"
                fullWidth
                className={classes.textField}
                helperText="Copia la ruta desde el Administrador de Archivos"
              />
              <FormControl variant="outlined" margin="dense" fullWidth className={classes.formControl}>
                <InputLabel id="whatsapp-selection-label">Conexión de WhatsApp</InputLabel>
                <Field 
                  as={Select} 
                  labelId="whatsapp-selection-label"
                  label="Conexión de WhatsApp"
                  name="whatsappId"
                >
                  {whatsApps.map((whatsapp) => (
                    <MenuItem key={whatsapp.id} value={whatsapp.id}>
                      {whatsapp.name}
                    </MenuItem>
                  ))}
                </Field>
              </FormControl>
              <FormControl variant="outlined" margin="dense" fullWidth className={classes.formControl}>
                <InputLabel id="flow-selection-label">Flujo FlowBuilder (IA)</InputLabel>
                <Field 
                  as={Select} 
                  labelId="flow-selection-label"
                  label="Flujo FlowBuilder (IA)"
                  name="flowId"
                >
                  {flows.map((flow) => (
                    <MenuItem key={flow.id} value={flow.id}>
                      {flow.name}
                    </MenuItem>
                  ))}
                </Field>
              </FormControl>
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
              <Button onClick={onClose} disabled={isSubmitting} className={classes.secondaryButton}>
                {i18n.t("common.cancel")}
              </Button>
              <Button type="submit" disabled={isSubmitting} className={classes.applyButton}>
                {i18n.t("common.save")}
                {isSubmitting && <CircularProgress size={20} style={{ marginLeft: 8 }} />}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default WebWidgetModal;
